import React, { ReactElement, useEffect } from 'react'
import { useFormContext, UseFormMethods } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { DomainProfile as DomainProfileLanguage } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { isVarNotEmpty, parseBool } from '../../../NonReact/utils/GlobalFunctions'
import FormRow from '../../Common/Components/FormRow'
import Heading, { HeadingLevel } from '../../Common/Components/Heading'
import {
    CMEDomainNameSpacing,
    CXI_CLIENT_MAC_URL_FIELD_NAME,
    CXI_CLIENT_WINDOWS_URL_FIELD_NAME,
    DomainTabFieldNames,
    SEARCH_AND_CALL_URL_FIELD_NAME,
    TEAMS_APP_DOWNLOAD_URL_FIELD_NAME
} from '../FieldName'
import { AutocompleteValues } from '../../../NonReact/constants/FormConstants'
import { Rules } from '../../Common/Rules/Rules'
import { ValidationErrors } from '../../Common/Components/ValidationErrors/ValidationErrors'
import { CMEResponse } from '../../../NonReact/Types/Types'
import { EMPTY_STRING } from '../../../NonReact/constants/Separators'
import { OutlinedTextInput } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedTextInput'
import { RouterCheckbox } from '../../../ReactRouting/Components/Atoms/Inputs/RouterCheckbox'
import { OutlinedPasswordTextInput } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedPasswordTextInput'

type Props = {
    isViewOnly?: boolean
    isAdmin: boolean
    isCxiEnabled: boolean
    hasCxiCapability: boolean
    parentCME: Nullable<CMEResponse>
    isNewCME: boolean
}

const CXI_ACCESS_CODE_MAX_LENGTH: number = 20

/**
 * Report Modal - ACD Codes Tab component.
 */
const DomainProfile: React.FC<Props> = ({
    isViewOnly,
    isAdmin,
    isCxiEnabled,
    hasCxiCapability,
    parentCME,
    isNewCME
}: Props): ReactElement => {
    // Access language resources directly without context as this is top level component.
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle(DomainProfileLanguage.NAME)

    const { watch, setValue }: UseFormMethods = useFormContext()

    useEffect((): void => {
        if (isVarNotEmpty(parentCME) && isNewCME) {
            setValue(TEAMS_APP_DOWNLOAD_URL_FIELD_NAME, parentCME.AppDownloadURL ?? EMPTY_STRING)
            setValue(CXI_CLIENT_WINDOWS_URL_FIELD_NAME, parentCME.CXIClientDownloadURLWin ?? EMPTY_STRING)
            setValue(CXI_CLIENT_MAC_URL_FIELD_NAME, parentCME.CXIClientDownloadURLMac ?? EMPTY_STRING)
            setValue(SEARCH_AND_CALL_URL_FIELD_NAME, parentCME.SearchAndCallAppDownloadURL ?? EMPTY_STRING)
        }
    }, [parentCME, isNewCME])

    return (
        <div className='domain-profile-tab tab-area'>
            <FormRow>
                <OutlinedTextInput
                    labelLeft={language.get(DomainProfileLanguage.Domain)}
                    inputName={CMEDomainNameSpacing(DomainTabFieldNames.DOMAIN)}
                    disabled={isViewOnly}
                />
            </FormRow>
            <FormRow>
                <Heading level={HeadingLevel.ONE}>
                    {language.get(DomainProfileLanguage.EmailServerConnectivity)}
                </Heading>
                <OutlinedTextInput
                    labelLeft={language.get(DomainProfileLanguage.ServerIpAddress)}
                    inputName={CMEDomainNameSpacing(DomainTabFieldNames.SERVER_IP_ADDRESS)}
                    disabled={isViewOnly}
                />
            </FormRow>
            <FormRow>
                <OutlinedTextInput
                    labelLeft={language.get(DomainProfileLanguage.TcpPort)}
                    inputName={CMEDomainNameSpacing(DomainTabFieldNames.TCP_PORT)}
                    disabled={isViewOnly}
                />
            </FormRow>
            <FormRow>
                <RouterCheckbox
                    className='additional-padding-top'
                    labelLeft={language.get(DomainProfileLanguage.CommunicationActive)}
                    name={CMEDomainNameSpacing(DomainTabFieldNames.COMMUNICATION_ACTIVE)}
                    disabled={isViewOnly}
                />
            </FormRow>
            <FormRow>
                <OutlinedTextInput
                    labelLeft={language.get(DomainProfileLanguage.FromAddressEmail)}
                    inputName={CMEDomainNameSpacing(DomainTabFieldNames.FROM_ADDRESS_EMAIL)}
                    disabled={isViewOnly}
                />
            </FormRow>
            <FormRow>
                <OutlinedTextInput
                    labelLeft={language.get(DomainProfileLanguage.FromAddressName)}
                    inputName={CMEDomainNameSpacing(DomainTabFieldNames.FROM_ADDRESS_NAME)}
                    disabled={isViewOnly}
                />
            </FormRow>
            <FormRow>
                <OutlinedTextInput
                    labelLeft={language.get(DomainProfileLanguage.CompanyName)}
                    inputName={CMEDomainNameSpacing(DomainTabFieldNames.COMPANY_NAME)}
                    disabled={isViewOnly}
                />
            </FormRow>
            <FormRow>
                <RouterCheckbox
                    className='additional-padding-top'
                    labelLeft={language.get(DomainProfileLanguage.UseAuthentication)}
                    name={CMEDomainNameSpacing(DomainTabFieldNames.USE_AUTHENTICATION)}
                    disabled={isViewOnly}
                />
            </FormRow>
            <FormRow>
                <Heading level={HeadingLevel.ONE}>{language.get(DomainProfileLanguage.AuthenticationSettings)}</Heading>
                <OutlinedTextInput
                    labelLeft={language.get(DomainProfileLanguage.DomainProfile_Username)}
                    inputName={CMEDomainNameSpacing(DomainTabFieldNames.USERNAME)}
                    disabled={
                        isViewOnly || !parseBool(watch(CMEDomainNameSpacing(DomainTabFieldNames.USE_AUTHENTICATION)))
                    }
                />
            </FormRow>
            <FormRow>
                <OutlinedPasswordTextInput
                    labelLeft={language.get(DomainProfileLanguage.DomainProfile_Password)}
                    inputName={CMEDomainNameSpacing(DomainTabFieldNames.PASSWORD)}
                    autoComplete={AutocompleteValues.NEW_PASSWORD}
                    placeholder={language.get(DomainProfileLanguage.DomainProfile_PasswordPlaceholder)}
                    isRandomiserEnabled={false}
                    isShowPasswordEnabled={true}
                    isPasswordCheckerEnabled={false}
                    disabled={
                        isViewOnly || !parseBool(watch(CMEDomainNameSpacing(DomainTabFieldNames.USE_AUTHENTICATION)))
                    }
                />
            </FormRow>
            <ValidationErrors name={TEAMS_APP_DOWNLOAD_URL_FIELD_NAME}>
                <FormRow>
                    <Heading level={HeadingLevel.ONE}>
                        {language.get(DomainProfileLanguage.CustomDownloadLinks)}
                    </Heading>
                    <OutlinedTextInput
                        labelLeft={language.get(DomainProfileLanguage.MSTeamsAppDownloadLinkURLName)}
                        inputName={TEAMS_APP_DOWNLOAD_URL_FIELD_NAME}
                        disabled={isViewOnly || !isAdmin}
                        rules={{
                            validate: {
                                isValidUrl: Rules.isValidUrl(language),
                                isEmpty: Rules.isEmpty(language)
                            }
                        }}
                    />
                </FormRow>
            </ValidationErrors>
            {isCxiEnabled && hasCxiCapability ? (
                <>
                    <ValidationErrors name={CXI_CLIENT_WINDOWS_URL_FIELD_NAME}>
                        <FormRow>
                            <OutlinedTextInput
                                labelLeft={language.get(DomainProfileLanguage.CXIClientDownloadLinkWindowsUrlName)}
                                inputName={CXI_CLIENT_WINDOWS_URL_FIELD_NAME}
                                disabled={isViewOnly || !isAdmin}
                                rules={{
                                    validate: {
                                        isValidUrl: Rules.isValidUrl(language)
                                    }
                                }}
                            />
                        </FormRow>
                    </ValidationErrors>
                    <ValidationErrors name={CXI_CLIENT_MAC_URL_FIELD_NAME}>
                        <FormRow>
                            <OutlinedTextInput
                                labelLeft={language.get(DomainProfileLanguage.CXIClientDownloadLinkMacUrlName)}
                                inputName={CXI_CLIENT_MAC_URL_FIELD_NAME}
                                disabled={isViewOnly || !isAdmin}
                                rules={{
                                    validate: {
                                        isValidUrl: Rules.isValidUrl(language)
                                    }
                                }}
                            />
                        </FormRow>
                    </ValidationErrors>
                    <ValidationErrors name={SEARCH_AND_CALL_URL_FIELD_NAME}>
                        <FormRow>
                            <OutlinedTextInput
                                labelLeft={language.get(DomainProfileLanguage.SearchAndCallDownloadLinkURLName)}
                                inputName={SEARCH_AND_CALL_URL_FIELD_NAME}
                                disabled={isViewOnly || !isAdmin}
                                rules={{
                                    validate: {
                                        isValidUrl: Rules.isValidUrl(language)
                                    }
                                }}
                            />
                        </FormRow>
                    </ValidationErrors>
                </>
            ) : (
                <></>
            )}
            {hasCxiCapability && isCxiEnabled ? (
                <>
                    <Heading level={HeadingLevel.ONE}>{language.get(DomainProfileLanguage.AccessCodes)}</Heading>
                    <ValidationErrors name={CMEDomainNameSpacing(DomainTabFieldNames.CXI_ACCESS_CODE)}>
                        <FormRow>
                            <OutlinedTextInput
                                labelLeft={language.get(DomainProfileLanguage.CXIAccessCode)}
                                inputName={CMEDomainNameSpacing(DomainTabFieldNames.CXI_ACCESS_CODE)}
                                disabled={isViewOnly || !isAdmin}
                                maxLength={CXI_ACCESS_CODE_MAX_LENGTH}
                                rules={{ maxLength: CXI_ACCESS_CODE_MAX_LENGTH }}
                            />
                        </FormRow>
                    </ValidationErrors>
                </>
            ) : (
                <></>
            )}
        </div>
    )
}

export default DomainProfile
